import BugTable from './BugTable'
import type { BugListItem, SecurityProbeGroupSummary } from '../data/projectTypes'

export function BugCount({ ids }: { ids: string[] }) {
  return ids.length > 0 ? <span className="security-tab__bug-count">{ids.length} bug{ids.length === 1 ? '' : 's'}</span> : null
}

export function requestCount(count: number): string {
  return `${count} request${count === 1 ? '' : 's'}`
}

/**
 * The bugs/requests pair on an API row of the Pentest Scan list. Under the list's column headings it
 * is two cells carrying the bare numbers, the way the test-run table's Bugs column does. Where those
 * headings are hidden (narrow viewports, where the row stacks) it collapses to a single pair of pills
 * that say what they count, since nothing else would.
 *
 * Zero requests stays amber either way: an endpoint the probe runner never reached is worth noticing.
 */
export function TargetCounts({ ids, requests, inline }: { ids: string[]; requests: number; inline: boolean }) {
  const requestClass = requests ? 'security-tab__examined' : 'security-tab__not-examined'

  if (inline) {
    return (
      <span className="security-tab__counts">
        <BugCount ids={ids} />
        <span className={requestClass}>{requestCount(requests)}</span>
      </span>
    )
  }

  return (
    <>
      {ids.length > 0
        ? <span className="security-tab__bug-count">{ids.length}</span>
        : <span className="security-tab__zero-count">0</span>}
      <span className={requestClass}>{requests}</span>
    </>
  )
}

export function ProbeGroups({ groups, projectId, showDetails, bugs, hasWebhook }: { groups: SecurityProbeGroupSummary[]; projectId: string; showDetails: boolean; bugs: BugListItem[]; hasWebhook: boolean }) {
  return <div className="security-tab__probe-groups">{groups.map(group => (
    <details className="security-tab__probe-group" key={group.operation}>
      <summary>
        <span>{group.label}</span>
        <span className="security-tab__counts"><BugCount ids={group.bug_ids} /><span className={group.request_count ? 'security-tab__examined' : 'security-tab__not-examined'}>{requestCount(group.request_count)}</span></span>
      </summary>
      <div className="security-tab__probe-group-detail">
        <p>{group.description}</p>
        {group.bug_ids.length > 0 && <BugTable bugs={bugs.filter(bug => group.bug_ids.includes(bug.id))} projectId={projectId} loading={false} hasWebhook={hasWebhook} showKind={false} showEnvironment={false} showSecurityImpact />}
        {showDetails && group.requests && group.requests.length > 0 && <details className="security-tab__request-details">
          <summary>Request details ({group.request_count})</summary>
          <ol className="security-tab__requests">{group.requests.map((request, index) => (
            <li key={index}>
              <div><code>{request.method}</code><code>{request.url}</code><span>{request.status == null ? 'No response' : `HTTP ${request.status}`}</span></div>
              {request.narration !== 'Security probe request' && <p>{request.narration}</p>}
              {request.error && <p>{request.error}</p>}
            </li>
          ))}</ol>
        </details>}
      </div>
    </details>
  ))}</div>
}
